import AddIcon from "@mui/icons-material/Add"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  CircularProgress,
  styled,
  Typography,
} from "@mui/material"
import type { BiomeshEventResponse } from "@phc/common/gen/threatAnalyticsGen"
import { useListEventsBiomeshEventListGet } from "@phc/common/gen/threatAnalyticsGen"
import { useState } from "react"
import { extraColors } from "../../../utils/theme"
import { DeleteEventDialog } from "../../Cop/DeleteEventDialog"
import { EventFormDialog } from "../../Cop/EventFormDialog"

import { EventCard } from "./EventCard"
import { SectionLabel } from "./shared"

const EventsAccordion = styled(Accordion)(({ theme }) => ({
  backgroundColor: "transparent",
  boxShadow: "none",
  "&:before": { display: "none" },
  margin: 0,
  "& .MuiAccordionSummary-root": {
    minHeight: 0,
    padding: 0,
  },
  "& .MuiAccordionSummary-content": {
    margin: 0,
    alignItems: "center",
    gap: theme.spacing(1),
  },
  "& .MuiAccordionDetails-root": {
    padding: 0,
    paddingTop: theme.spacing(1.5),
  },
}))

export function SensorEventsSection({ sensorId }: { sensorId: string }) {
  const [expanded, setExpanded] = useState(false)
  const [addEventOpen, setAddEventOpen] = useState(false)
  const [editEvent, setEditEvent] = useState<BiomeshEventResponse | null>(null)
  const [deleteEvent, setDeleteEvent] = useState<BiomeshEventResponse | null>(
    null
  )

  const { data: events, isLoading: eventsLoading } =
    useListEventsBiomeshEventListGet(
      { sensor_id: sensorId, limit: 1000 },
      { query: { enabled: expanded } }
    )

  return (
    <>
      <EventsAccordion
        disableGutters
        expanded={expanded}
        onChange={(_, open) => setExpanded(open)}
      >
        <AccordionSummary
          expandIcon={
            <ExpandMoreIcon fontSize="small" sx={{ color: extraColors.hint }} />
          }
        >
          <SectionLabel variant="small1Bold" sx={{ mb: 0 }}>
            Events
          </SectionLabel>
          {events && events.length > 0 && (
            <Chip
              label={events.length}
              size="small"
              sx={{
                height: 18,
                fontSize: 11,
                bgcolor: extraColors.dark20,
                color: extraColors.medium,
              }}
            />
          )}
        </AccordionSummary>
        <AccordionDetails>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setAddEventOpen(true)}
            sx={{ mb: 1 }}
          >
            Add Event
          </Button>
          {eventsLoading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={20} />
            </Box>
          )}
          {!eventsLoading && events?.length === 0 && (
            <Typography
              variant="small1"
              sx={{
                color: extraColors.hint,
                textAlign: "center",
                display: "block",
                py: 1,
              }}
            >
              No events recorded
            </Typography>
          )}
          {events?.map(evt => (
            <EventCard
              key={evt.event_id}
              event={evt}
              onEdit={() => setEditEvent(evt)}
              onDelete={() => setDeleteEvent(evt)}
            />
          ))}
        </AccordionDetails>
      </EventsAccordion>

      {addEventOpen && (
        <EventFormDialog
          open
          onClose={() => setAddEventOpen(false)}
          sensorId={sensorId}
        />
      )}
      {editEvent && (
        <EventFormDialog
          key={editEvent.event_id}
          open
          onClose={() => setEditEvent(null)}
          sensorId={sensorId}
          event={editEvent}
        />
      )}
      {deleteEvent && (
        <DeleteEventDialog
          open
          onClose={() => setDeleteEvent(null)}
          event={deleteEvent}
        />
      )}
    </>
  )
}
